import { useEffect, useRef, useCallback } from 'react';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { useRoute } from '@/context/RouteContext';

// Default center (Oklahoma)
const DEFAULT_CENTER = { lat: 35.4676, lng: -97.5164 };
const DEFAULT_ZOOM = 8;

// Marker colors
const MARKER_COLORS = {
  start: '#22C55E',    // Green
  subject: '#FFFFFF',  // White
  address: '#F59E0B',  // Amber
  finish: '#FF3B30',   // Red
};

const MARKER_LABELS = {
  start: 'S',
  subject: 'SP',
  finish: 'F',
};

const MapContent = () => {
  const map = useMap();
  const {
    startAddress,
    subjectProperty,
    comparables,
    finishAddress,
    routeResult,
  } = useRoute();

  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const directionsRendererRef = useRef(null);

  // Clear existing markers
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, []);

  // Get label for marker type
  const getTypeLabel = (type, label) => {
    switch (type) {
      case 'start': return 'Start';
      case 'subject': return 'Subject Property';
      case 'finish': return 'Finish';
      case 'address': return `Address ${label}`;
      default: return '';
    }
  };

  // Create custom marker with click/hover info window
  const createMarker = useCallback((position, type, label, address) => {
    if (!map || !window.google) return null;

    const color = MARKER_COLORS[type];
    const markerLabel = MARKER_LABELS[type] || label;
    
    // Subject property needs dark text on white background
    const labelColor = type === 'subject' ? '#000000' : '#FFFFFF';
    const strokeColor = type === 'subject' ? '#D946EF' : '#FFFFFF';
    const strokeWeight = type === 'subject' ? 3 : 2;

    // Larger markers for better mobile tap targets
    const markerScale = type === 'subject' ? 20 : 18;

    const marker = new window.google.maps.Marker({
      position,
      map,
      label: {
        text: markerLabel,
        color: labelColor,
        fontWeight: 'bold',
        fontSize: type === 'subject' ? '11px' : '13px',
      },
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: markerScale,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: strokeColor,
        strokeWeight: strokeWeight,
      },
      zIndex: type === 'subject' ? 1000 : 100,
      clickable: true,
      cursor: 'pointer',
    });

    markersRef.current.push(marker);

    // Create info window content
    const typeLabel = getTypeLabel(type, label);
    const infoContent = `
      <div style="padding: 12px; font-family: system-ui, sans-serif; min-width: 180px;">
        <div style="font-weight: 600; font-size: 15px; color: #333; margin-bottom: 6px;">
          ${typeLabel}
        </div>
        <div style="font-size: 14px; color: #555; line-height: 1.4;">
          ${address || 'No address'}
        </div>
      </div>
    `;

    // Create a single info window instance for this marker
    const infoWindow = new window.google.maps.InfoWindow({
      content: infoContent,
      maxWidth: 280,
    });

    // Show info window on click (works on both desktop and mobile)
    marker.addListener('click', () => {
      // Close any existing info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Open this info window
      infoWindow.open(map, marker);
      infoWindowRef.current = infoWindow;
    });

    // Also show on hover for desktop only
    marker.addListener('mouseover', () => {
      // Only trigger on desktop (not touch devices)
      if (window.matchMedia('(hover: hover)').matches) {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        infoWindow.open(map, marker);
        infoWindowRef.current = infoWindow;
      }
    });

    return marker;
  }, [map]);

  // Update markers when addresses change
  useEffect(() => {
    if (!map || !window.google) return;

    clearMarkers();

    const bounds = new window.google.maps.LatLngBounds();
    let hasMarkers = false;

    if (startAddress.location) {
      createMarker(startAddress.location, 'start', null, startAddress.address);
      bounds.extend(startAddress.location);
      hasMarkers = true;
    }

    if (subjectProperty.location) {
      createMarker(subjectProperty.location, 'subject', null, subjectProperty.address);
      bounds.extend(subjectProperty.location);
      hasMarkers = true;
    }

    comparables.forEach((comp, index) => {
      if (comp.location) {
        createMarker(comp.location, 'address', `${index + 1}`, comp.address);
        bounds.extend(comp.location);
        hasMarkers = true;
      }
    });

    if (finishAddress.location) {
      createMarker(finishAddress.location, 'finish', null, finishAddress.address);
      bounds.extend(finishAddress.location);
      hasMarkers = true;
    }

    if (hasMarkers) {
      map.fitBounds(bounds, { padding: 80 });
    }
  }, [map, startAddress, subjectProperty, comparables, finishAddress, clearMarkers, createMarker]);

  // Render directions when route result changes
  useEffect(() => {
    if (!map || !window.google) return;

    // Clear existing renderer
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }

    if (!routeResult) return;

    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      map,
      directions: routeResult,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#2563EB',
        strokeWeight: 4,
        strokeOpacity: 0.8,
      },
    });
  }, [map, routeResult]);

  return null;
};

export const MapView = () => {
  return (
    <div className="w-full h-full" data-testid="map-container">
      <Map
        style={{ width: '100%', height: '100%' }}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling="greedy"
        disableDefaultUI={false}
        zoomControl={true}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={true}
        mapId="routescout-map"
      >
        <MapContent />
      </Map>
    </div>
  );
};
